import React, { useState, useEffect } from 'react';
import { cricketService } from '../../services/cricketAPI';
import MatchCard from './MatchCard';
import '../../styles/cricket/CricketDashboard.css';

const CricketDashboard = () => {
    const [matches, setMatches] = useState([]);
    const [filteredMatches, setFilteredMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        competition: 'all',
        search: '',
        inPlay: 'all'
    });
    const [competitions, setCompetitions] = useState([]);

    // Fetch matches on component mount
    useEffect(() => {
        fetchMatches();
        
        // Set up auto-refresh every 30 seconds
        const interval = setInterval(fetchMatches, 30000);
        return () => clearInterval(interval);
    }, []);

    // Apply filters whenever matches or filters change
    useEffect(() => {
        applyFilters();
    }, [matches, filters]);

    // Extract unique competitions from matches
    useEffect(() => {
        if (matches.length > 0) {
            const uniqueCompetitions = [...new Set(matches.map(m => m.cname))];
            setCompetitions(uniqueCompetitions);
        }
    }, [matches]);

    const fetchMatches = async () => {
        try {
            setLoading(true);
            const data = await cricketService.getAllMatches();
            setMatches(data.sport.body);
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            setError('Failed to fetch cricket matches. Please try again later.');
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...matches];

        // Filter by status (OPEN/SUSPENDED)
        if (filters.status !== 'all') {
            filtered = filtered.filter(m => m.status === filters.status);
        }

        // Filter by inPlay
        if (filters.inPlay !== 'all') {
            const inPlayValue = filters.inPlay === 'live';
            filtered = filtered.filter(m => m.inPlay === inPlayValue);
        }

        // Filter by competition
        if (filters.competition !== 'all') {
            filtered = filtered.filter(m => m.cname === filters.competition);
        }

        // Search by match name
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(m => 
                m.matchName.toLowerCase().includes(searchLower)
            );
        }

        setFilteredMatches(filtered);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            status: 'all',
            competition: 'all',
            search: '',
            inPlay: 'all'
        });
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading && matches.length === 0) {
        return (
            <div className="cricket-loading">
                <div className="spinner"></div>
                <p>Loading cricket matches...</p>
            </div>
        );
    }

    return (
        <div className="cricket-dashboard">
            {/* Header */}
            <div className="cricket-header">
                <h1>
                    <span className="icon">🏏</span> 
                    Cricket Matches
                </h1>
                <div className="header-controls">
                    <div className="last-updated">
                        Last updated: {lastUpdated?.toLocaleTimeString() || 'Never'}
                    </div>
                    <button 
                        className="refresh-btn"
                        onClick={fetchMatches}
                        disabled={loading}
                    >
                        🔄 Refresh
                    </button>
                </div>
            </div>

            {/* Filters Section */}
            <div className="filters-section">
                <div className="filter-group">
                    <select 
                        value={filters.inPlay}
                        onChange={(e) => handleFilterChange('inPlay', e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Matches</option>
                        <option value="live">Live Now</option>
                        <option value="upcoming">Upcoming</option>
                    </select>

                    <select 
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Status</option>
                        <option value="OPEN">Open</option>
                        <option value="SUSPENDED">Suspended</option>
                    </select>

                    <select 
                        value={filters.competition}
                        onChange={(e) => handleFilterChange('competition', e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Competitions</option>
                        {competitions.map(comp => (
                            <option key={comp} value={comp}>{comp}</option>
                        ))}
                    </select>

                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Search teams..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="search-input"
                        />
                        {filters.search && (
                            <button 
                                className="clear-search"
                                onClick={() => handleFilterChange('search', '')}
                            >
                                ×
                            </button>
                        )}
                    </div>

                    <button 
                        className="clear-filters-btn"
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </button>
                </div>

                {/* Results count */}
                <div className="results-count">
                    Showing {filteredMatches.length} of {matches.length} matches
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    {error}
                </div>
            )}

            {/* Matches Grid */}
            <div className="matches-grid">
                {filteredMatches.length > 0 ? (
                    filteredMatches.map(match => (
                        <MatchCard 
                            key={match.marketid ?? match.marketId} 
                            match={match}
                            formatDateTime={formatDateTime}
                        />
                    ))
                ) : (
                    <div className="no-matches">
                        <p>No matches found matching your filters.</p>
                        <button onClick={clearFilters} className="clear-filters-link">
                            Clear filters
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CricketDashboard;